/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  useCallback,
  useRef,
  useState,
  useEffect,
  useMemo,
  type ChangeEvent,
  type DragEvent,
  type InputHTMLAttributes,
} from 'react';
import {
  convertMetadataToFileWithPreview,
  processFilesForAddition,
  revokeImageObjectURLs,
} from './use-file-upload.utils';

/**
 * Metadata for files that have already been uploaded to the server
 * Used for displaying existing files (e.g., when editing a form with pre-uploaded files)
 */
export type FileMetadata = {
  name: string; // File name
  size: number; // File size in bytes
  type: string; // MIME type (e.g., "image/png", "application/pdf")
  url: string; // URL to access the file (for preview/display)
  id: string; // Unique identifier for the file
};

/**
 * File object with preview and metadata
 * Can represent either a new File object (from user selection) or existing FileMetadata (from server)
 */
export type FileWithPreview = {
  file: File | FileMetadata; // The actual file or metadata
  id: string; // Unique identifier for tracking the file
  preview?: string; // Preview URL (object URL for new files, server URL for existing files)
  isFile: boolean; // Whether this is a File instance (true) or FileMetadata (false)
};

/**
 * Configuration options for the file upload hook
 */
export type FileUploadOptions = {
  maxFiles?: number; // Maximum number of files allowed (only used when multiple is true, defaults to Infinity)
  maxSize?: number; // Maximum file size in bytes (defaults to Infinity)
  accept?: string; // Accepted file types (e.g., "image/*", ".pdf,.doc", defaults to "*" for all files)
  multiple?: boolean; // Whether to allow multiple file selection (defaults to false)
  initialFiles?: FileMetadata[]; // Pre-populated files (e.g., when editing existing content)
  onFilesChange?: (files: FileWithPreview[]) => void; // Callback fired whenever the files list changes
  onFilesAdded?: (addedFiles: FileWithPreview[]) => void; // Callback fired when new files are successfully added
  onFileRemoved?: (removedFile: FileWithPreview) => void; // Callback fired when a file is removed
};

/**
 * Current state of the file upload system
 */
export type FileUploadState = {
  files: FileWithPreview[]; // Array of all currently selected/uploaded files
  isDragging: boolean; // Whether user is currently dragging files over the drop zone
  errors: string[]; // Array of validation error messages
};

/**
 * Actions available for managing file uploads
 */
export type FileUploadActions = {
  addFiles: (files: FileList | File[]) => void; // Programmatically add files to the list
  removeFile: (id: string) => void; // Remove a file by its unique ID
  clearFiles: () => void; // Remove all files and clear errors
  clearErrors: () => void; // Clear validation errors without removing files
  handleDragEnter: (e: DragEvent<HTMLElement>) => void; // Drag event handler for entering drop zone
  handleDragLeave: (e: DragEvent<HTMLElement>) => void; // Drag event handler for leaving drop zone
  handleDragOver: (e: DragEvent<HTMLElement>) => void; // Drag event handler for hovering over drop zone
  handleDrop: (e: DragEvent<HTMLElement>) => void; // Drag event handler for dropping files
  handleFileChange: (e: ChangeEvent<HTMLInputElement>) => void; // Input change handler for file selection
  openFileDialog: () => void; // Programmatically open the file selection dialog
  getInputProps: (props?: InputHTMLAttributes<HTMLInputElement>) => InputHTMLAttributes<HTMLInputElement> & {
    // Returns props to spread on a file input element
    // Use `any` here to avoid cross-React ref type conflicts across packages
    ref: any;
  };
};

/**
 * Custom hook for managing file upload functionality
 *
 * This hook handles:
 * - File selection via input dialog or drag-and-drop
 * - File validation (size, type, count limits)
 * - Preview generation for images
 * - Managing file state (add, remove, clear)
 * - Supporting both single and multiple file modes
 * - Handling existing files (FileMetadata) and new files (File)
 * - Memory management (cleanup of object URLs)
 *
 * Features:
 * - Drag and drop support
 * - File type validation
 * - File size validation
 * - Duplicate file detection
 * - Preview URLs for images
 * - Error handling and reporting
 * - Callback support for file lifecycle events
 *
 * @param options - Configuration options for file upload behavior
 * @returns Tuple containing [state, actions] for managing file uploads
 *
 * @example
 * ```tsx
 * const [state, actions] = useFileUpload({
 *   maxFiles: 5,
 *   maxSize: 10 * 1024 * 1024, // 10MB
 *   accept: 'image/*',
 *   multiple: true,
 *   onFilesChange: (files) => console.log('Files changed:', files),
 * });
 *
 * return (
 *   <div onDrop={actions.handleDrop} onDragOver={actions.handleDragOver}>
 *     <input {...actions.getInputProps()} />
 *     <button onClick={actions.openFileDialog}>Select Files</button>
 *   </div>
 * );
 * ```
 */
export const useFileUpload = (options: FileUploadOptions = {}): [FileUploadState, FileUploadActions] => {
  // ===== OPTIONS =====
  // Extract and set defaults for all configuration options
  const {
    maxFiles = Infinity, // No limit by default
    maxSize = Infinity, // No size limit by default
    accept = '*', // Accept all file types by default
    multiple = false, // Single file mode by default
    initialFiles = [], // No initial files by default
    onFilesChange, // Optional callback for file changes
    onFilesAdded, // Optional callback for new files
    onFileRemoved, // Optional callback for removed files
  } = options;

  // ===== REFS =====
  // Ref to the hidden file input element (used for programmatic file selection)
  const inputRef = useRef<HTMLInputElement>(null);

  // ===== STATE =====
  /**
   * Main state containing:
   * - files: Array of all selected/uploaded files
   * - isDragging: Whether user is dragging files over drop zone
   * - errors: Array of validation error messages
   */
  const [state, setState] = useState<FileUploadState>({
    files: convertMetadataToFileWithPreview(initialFiles),
    isDragging: false, // No drag operation in progress initially
    errors: [], // No errors initially
  });

  // ===== MEMOIZATION =====
  /**
   * Memoize initial files to prevent unnecessary re-renders
   * Only recalculates when initialFiles array reference changes
   */
  const memoizedInitialFiles = useMemo(() => convertMetadataToFileWithPreview(initialFiles), [initialFiles]);

  // ===== TRACKING REFS =====
  // Track previous initial files to detect actual changes (not just reference changes)
  const prevInitialFilesRef = useRef<FileWithPreview[]>([]);

  // ===== EFFECTS =====
  /**
   * Sync files state when initialFiles prop changes
   *
   * This effect:
   * - Detects when initialFiles actually changes (by comparing IDs, not just reference)
   * - Updates internal state to match new initialFiles
   * - Uses setTimeout to defer state update (prevents setState during render warnings)
   * - Cleans up timeout on unmount or when dependencies change
   *
   * This is useful when editing existing content that has pre-uploaded files
   */
  useEffect(() => {
    // Check if initialFiles actually changed by comparing with previous value
    // This prevents unnecessary updates when the array reference changes but content is the same
    const hasChanged =
      prevInitialFilesRef.current.length !== memoizedInitialFiles.length ||
      prevInitialFilesRef.current.some((prevFile, index) => {
        const currentFile = memoizedInitialFiles[index];
        // If file count differs or any file ID changed, consider it a change
        return !currentFile || prevFile.id !== currentFile.id;
      });

    if (hasChanged) {
      // Use setTimeout to defer the state update to avoid setState during render
      // This prevents React warnings about updating state during render phase
      const timeoutId = setTimeout(() => {
        setState((prev) => ({
          ...prev,
          files: memoizedInitialFiles, // Update files to match new initialFiles
        }));
        prevInitialFilesRef.current = memoizedInitialFiles; // Track new value
      }, 0);

      // Cleanup: Clear timeout if component unmounts or effect re-runs
      return () => clearTimeout(timeoutId);
    }
  }, [memoizedInitialFiles]);

  // ===== HELPER FUNCTIONS =====
  // Helper functions are now in use-file-upload.utils.ts for reusability

  /**
   * Clears all files and errors
   *
   * This function:
   * - Revokes all object URLs to prevent memory leaks
   * - Clears the file input value
   * - Resets files array to empty
   * - Clears all error messages
   * - Calls onFilesChange callback with empty array
   *
   * Important: Always revokes object URLs before clearing to prevent memory leaks
   */
  const clearFiles = useCallback(() => {
    setState((prev) => {
      // Clean up object URLs for image files
      revokeImageObjectURLs(prev.files);

      // Reset file input value to allow selecting the same file again
      if (inputRef.current) {
        inputRef.current.value = '';
      }

      const newState = {
        ...prev,
        files: [], // Clear all files
        errors: [], // Clear all errors
      };

      // Notify parent component that files have changed
      onFilesChange?.(newState.files);
      return newState;
    });
  }, [onFilesChange]);

  /**
   * Adds new files to the upload list
   *
   * This function handles the complete file addition flow:
   * 1. Validates files against constraints (size, type, count, duplicates)
   * 2. Creates preview URLs for valid files
   * 3. Generates unique IDs for tracking
   * 4. Updates state with valid files
   * 5. Reports validation errors
   * 6. Calls appropriate callbacks
   *
   * Validation checks:
   * - File count limit (in multiple mode)
   * - Duplicate files (in multiple mode)
   * - File size limit
   * - File type/extension
   *
   * In single file mode:
   * - Clears existing files before adding new one
   * - Only the first file is kept if multiple are provided
   *
   * In multiple file mode:
   * - Appends new files to existing list
   * - Checks total file count doesn't exceed maxFiles
   * - Detects and rejects duplicate files
   *
   * @param newFiles - FileList or File array to add
   */
  const addFiles = useCallback(
    (newFiles: FileList | File[]) => {
      // Early return if no files provided
      if (!newFiles || newFiles.length === 0) return;

      // Convert FileList to array for easier manipulation
      const newFilesArray = Array.from(newFiles);

      // Clear existing errors when new files are uploaded
      setState((prev) => ({ ...prev, errors: [] }));

      // In single file mode, clear existing files first
      if (!multiple) {
        clearFiles();
      }

      // Process files using utility function
      const { validFiles, errors } = processFilesForAddition(newFilesArray, {
        existingFiles: state.files,
        maxFiles,
        maxSize,
        accept,
        multiple,
      });

      // Only update state if we have valid files to add
      if (validFiles.length > 0) {
        // Call the onFilesAdded callback with the newly added valid files
        onFilesAdded?.(validFiles);

        setState((prev) => {
          // In single file mode, replace all files with new valid files
          // In multiple mode, append new files to existing list
          const updatedFiles = !multiple ? validFiles : [...prev.files, ...validFiles];

          // Notify parent component of file changes
          onFilesChange?.(updatedFiles);

          return {
            ...prev,
            files: updatedFiles,
            errors, // Include any validation errors (for files that failed)
          };
        });
      } else if (errors.length > 0) {
        // If no valid files but we have errors, update state with errors only
        setState((prev) => ({
          ...prev,
          errors,
        }));
      }

      // Reset input value after handling files
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    },
    [state.files, maxFiles, multiple, maxSize, accept, clearFiles, onFilesChange, onFilesAdded]
  );

  /**
   * Removes a file from the upload list by its unique ID
   *
   * This function:
   * - Finds the file by ID
   * - Revokes object URL if it's an image file (prevents memory leaks)
   * - Removes file from state
   * - Clears errors (fresh start after removal)
   * - Calls onFilesChange and onFileRemoved callbacks
   *
   * @param id - Unique identifier of the file to remove
   */
  const removeFile = useCallback(
    (id: string) => {
      setState((prev) => {
        // Find the file to remove
        const fileToRemove = prev.files.find((file) => file.id === id);

        // Clean up object URL for image files to prevent memory leaks
        if (fileToRemove) {
          revokeImageObjectURLs([fileToRemove]);
        }

        // Remove file from list
        const newFiles = prev.files.filter((file) => file.id !== id);

        // Notify parent component of file changes
        onFilesChange?.(newFiles);

        // Call the onFileRemoved callback with the removed file
        if (fileToRemove) {
          onFileRemoved?.(fileToRemove);
        }

        return {
          ...prev,
          files: newFiles,
          errors: [], // Clear errors after removal
        };
      });
    },
    [onFilesChange, onFileRemoved]
  );

  /**
   * Clears all validation errors without removing files
   *
   * Useful for allowing users to dismiss error messages
   * while keeping their selected files intact
   */
  const clearErrors = useCallback(() => {
    setState((prev) => ({
      ...prev,
      errors: [], // Clear errors only
    }));
  }, []);

  // ===== EVENT HANDLERS =====
  /**
   * Handles drag enter event (when user drags files into drop zone)
   *
   * Sets isDragging state to true to provide visual feedback
   * (e.g., highlight drop zone, change cursor)
   *
   * @param e - Drag event
   */
  const handleDragEnter = useCallback((e: DragEvent<HTMLElement>) => {
    e.preventDefault(); // Prevent default browser behavior
    e.stopPropagation(); // Stop event bubbling
    setState((prev) => ({ ...prev, isDragging: true })); // Update drag state
  }, []);

  /**
   * Handles drag leave event (when user drags files out of drop zone)
   *
   * Sets isDragging state to false to remove visual feedback.
   * Includes check to prevent false triggers when dragging over child elements.
   *
   * @param e - Drag event
   */
  const handleDragLeave = useCallback((e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();

    // Check if we're actually leaving the drop zone
    // If relatedTarget is still within currentTarget, we're just moving between child elements
    // This prevents flickering when dragging over nested elements
    if (e.currentTarget.contains(e.relatedTarget as Node)) {
      return; // Still within drop zone, don't update state
    }

    setState((prev) => ({ ...prev, isDragging: false })); // Update drag state
  }, []);

  /**
   * Handles drag over event (when user hovers files over drop zone)
   *
   * Prevents default behavior to allow drop event to fire.
   * Must be called to enable drop functionality.
   *
   * @param e - Drag event
   */
  const handleDragOver = useCallback((e: DragEvent<HTMLElement>) => {
    e.preventDefault(); // Required to allow drop
    e.stopPropagation();
  }, []);

  /**
   * Handles drop event (when user drops files into drop zone)
   *
   * This is where the actual file processing happens:
   * - Resets drag state
   * - Checks if input is disabled (respects disabled state)
   * - Extracts files from dataTransfer
   * - Calls addFiles with appropriate files (single vs multiple mode)
   *
   * @param e - Drag event containing dropped files
   */
  const handleDrop = useCallback(
    (e: DragEvent<HTMLElement>) => {
      e.preventDefault(); // Prevent default browser behavior (opening files)
      e.stopPropagation();
      setState((prev) => ({ ...prev, isDragging: false })); // Reset drag state

      // Don't process files if the input is disabled
      // This respects the disabled state of the file input
      if (inputRef.current?.disabled) {
        return;
      }

      // Extract files from drag event
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        // In single file mode, only use the first file
        if (!multiple) {
          const file = e.dataTransfer.files[0];
          addFiles([file]);
        } else {
          // In multiple mode, process all dropped files
          addFiles(e.dataTransfer.files);
        }
      }
    },
    [addFiles, multiple]
  );

  /**
   * Handles file input change event (when user selects files via file dialog)
   *
   * This is triggered when user clicks the file input and selects files.
   * Extracts files from the input element and passes them to addFiles.
   *
   * @param e - Change event from file input
   */
  const handleFileChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        addFiles(e.target.files); // Process selected files
      }
    },
    [addFiles]
  );

  /**
   * Programmatically opens the file selection dialog
   *
   * Useful for triggering file selection from a custom button or UI element
   * instead of requiring users to click the hidden file input directly.
   */
  const openFileDialog = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.click(); // Trigger click on hidden input
    }
  }, []);

  /**
   * Returns props to spread on a file input element
   *
   * This function provides a convenient way to configure the file input
   * with all necessary props, including:
   * - Type set to 'file'
   * - Change handler
   * - Accept pattern
   * - Multiple flag
   * - Ref to the input element
   *
   * Allows merging custom props while maintaining hook's configuration.
   *
   * @param props - Optional custom props to merge with default props
   * @returns Props object ready to spread on input element
   *
   * @example
   * ```tsx
   * <input {...actions.getInputProps({ className: 'custom-input' })} />
   * ```
   */
  const getInputProps = useCallback(
    (props: InputHTMLAttributes<HTMLInputElement> = {}) => {
      return {
        ...props, // Merge custom props first (allows overrides)
        type: 'file' as const, // Ensure type is 'file'
        onChange: handleFileChange, // Attach change handler
        accept: props.accept || accept, // Use custom accept or hook's accept
        multiple: props.multiple !== undefined ? props.multiple : multiple, // Use custom multiple or hook's multiple
        // Cast to `any` to prevent mismatched React ref type errors across workspaces
        ref: inputRef as any, // Attach ref to input element
      };
    },
    [accept, multiple, handleFileChange]
  );

  // ===== RETURN VALUES =====
  /**
   * Returns tuple containing state and actions
   *
   * State contains:
   * - files: Array of all selected/uploaded files
   * - isDragging: Whether user is dragging files over drop zone
   * - errors: Array of validation error messages
   *
   * Actions contain:
   * - File management: addFiles, removeFile, clearFiles
   * - Error management: clearErrors
   * - Drag and drop handlers: handleDragEnter, handleDragLeave, handleDragOver, handleDrop
   * - File input handlers: handleFileChange, openFileDialog, getInputProps
   */
  return [
    state,
    {
      addFiles, // Add files programmatically
      removeFile, // Remove a file by ID
      clearFiles, // Clear all files and errors
      clearErrors, // Clear errors only
      handleDragEnter, // Drag enter handler
      handleDragLeave, // Drag leave handler
      handleDragOver, // Drag over handler
      handleDrop, // Drop handler
      handleFileChange, // File input change handler
      openFileDialog, // Open file selection dialog
      getInputProps, // Get props for file input element
    },
  ];
};

// Re-export formatBytes for backward compatibility
export { formatBytes } from './use-file-upload.utils';
